// @ts-nocheck
import * as React from "react";
import * as V from "../api";
import {
  Badge, Button, Card, Empty, Field, Input, Loading, Modal, Select, TOKENS, Textarea,
  errorMessage, useToast,
} from "../ui";

// Phase 2 — 운영자 전용 Youth CRUD. 비운영자가 접근하면 안내만 노출.

export function YouthsPanel({ user }: { user: any | null }) {
  const toast = useToast();
  const [list, setList] = React.useState<V.Youth[] | null>(null);
  const [track, setTrack] = React.useState("");
  const [q, setQ]         = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const [editing, setEditing]   = React.useState<V.Youth | null>(null);

  const reload = React.useCallback(() => {
    V.youths.list({ track: track || undefined, q: q || undefined })
      .then((r) => setList(r.items))
      .catch((e) => {
        if (e.status === 403) setList([]);
        else { setList([]); toast.push(errorMessage(e), "danger"); }
      });
  }, [track, q, toast]);

  React.useEffect(() => { reload(); }, [reload]);

  if (!V.isOperator(user?.role)) {
    return <Empty title="운영자 전용 영역입니다." hint="시스템관리자/권역관리자/코디네이터만 접근 가능합니다." />;
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>청소년 명단</h2>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Input placeholder="이름 검색" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 160 }} />
          <Select value={track} onChange={(e) => setTrack(e.target.value)} style={{ width: 140 }}>
            <option value="">전체 트랙</option>
            <option value="general">일반</option>
            <option value="youth_council">공의 의원</option>
            <option value="care_subject">돌봄 대상</option>
          </Select>
          <Button onClick={() => setCreating(true)}>신규 등록</Button>
        </div>
      </div>

      {list === null ? <Loading /> :
        list.length === 0 ? <Empty title="등록된 청소년이 없습니다." /> :
        <div style={{ display: "grid", gap: 8 }}>
          {list.map((y) => (
            <Card key={y.id} onClick={() => setEditing(y)}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>
                    {y.name}
                    &nbsp;<Badge tone="info">{V.LABELS.youthTrack[y.track]}</Badge>
                    &nbsp;<Badge tone={y.status === "active" ? "success" : "neutral"}>
                      {V.LABELS.youthStatus[y.status]}
                    </Badge>
                  </div>
                  <div style={{ fontSize: 12, color: TOKENS.ink3, marginTop: 2 }}>
                    {y.school_name || "학교 미기재"} · {y.grade ? `${y.grade}학년` : "학년 미기재"} · {y.age != null ? `만 ${y.age}세` : "나이 미상"}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: TOKENS.ink3 }}>{fmt(y.created_at)}</div>
              </div>
            </Card>
          ))}
        </div>
      }

      <YouthEditor
        open={creating || !!editing}
        youth={editing}
        onClose={() => { setCreating(false); setEditing(null); }}
        onSaved={() => { setCreating(false); setEditing(null); reload(); }}
      />
    </div>
  );
}

function YouthEditor({ open, youth, onClose, onSaved }: {
  open: boolean; youth: V.Youth | null; onClose: () => void; onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName]       = React.useState("");
  const [track, setTrack]     = React.useState("general");
  const [grade, setGrade]     = React.useState("");
  const [birthOn, setBirthOn] = React.useState("");
  const [school, setSchool]   = React.useState("");
  const [address, setAddress] = React.useState("");
  const [status, setStatus]   = React.useState("active");
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setName(youth?.name || "");
    setTrack(youth?.track || "general");
    setGrade(youth?.grade?.toString() || "");
    setBirthOn(youth?.birth_on || "");
    setSchool(youth?.school_name || "");
    setAddress(youth?.address || "");
    setStatus(youth?.status || "active");
    setSubmitting(false);
  }, [open, youth]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload: any = {
        name, track, status,
        grade: grade ? Number(grade) : null,
        birth_on: birthOn || null,
        school_name: school || null,
        address: address || null,
      };
      if (youth) await V.youths.update(youth.id, payload);
      else       await V.youths.create(payload);
      toast.push(youth ? "청소년 정보를 저장했습니다." : "청소년을 등록했습니다.", "success");
      onSaved();
    } catch (e: any) {
      toast.push(errorMessage(e), "danger");
    } finally {
      setSubmitting(false);
    }
  };

  const destroy = async () => {
    if (!youth) return;
    if (!window.confirm(`${youth.name}을 삭제하시겠습니까? 되돌릴 수 없습니다.`)) return;
    try {
      await V.youths.destroy(youth.id);
      toast.push("삭제했습니다.", "success");
      onSaved();
    } catch (e: any) {
      toast.push(errorMessage(e), "danger");
    }
  };

  return (
    <Modal
      open={open}
      title={youth ? "청소년 정보 수정" : "청소년 신규 등록"}
      onClose={onClose}
      width={560}
      footer={
        <>
          {youth && <Button variant="danger" onClick={destroy}>삭제</Button>}
          <div style={{ flex: 1 }} />
          <Button variant="ghost" onClick={onClose}>취소</Button>
          <Button onClick={submit} disabled={submitting || !name.trim()}>
            {submitting ? "저장 중…" : "저장"}
          </Button>
        </>
      }
    >
      <Field label="이름" required>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label="트랙">
          <Select value={track} onChange={(e) => setTrack(e.target.value)}>
            <option value="general">일반</option>
            <option value="youth_council">공의 의원</option>
            <option value="care_subject">돌봄 대상</option>
          </Select>
        </Field>
        <Field label="상태">
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="active">활동 중</option>
            <option value="inactive">일시비활성</option>
            <option value="withdrawn">탈퇴</option>
          </Select>
        </Field>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label="학년 (1~12)">
          <Input type="number" min={1} max={12} value={grade} onChange={(e) => setGrade(e.target.value)} />
        </Field>
        <Field label="생년월일">
          <Input type="date" value={birthOn} onChange={(e) => setBirthOn(e.target.value)} />
        </Field>
      </div>
      <Field label="학교">
        <Input value={school} onChange={(e) => setSchool(e.target.value)} />
      </Field>
      <Field label="주소">
        <Textarea value={address} onChange={(e) => setAddress(e.target.value)} />
      </Field>
    </Modal>
  );
}

function fmt(iso: string) { try { return new Date(iso).toLocaleDateString("ko-KR"); } catch { return iso; } }
